// Crew avatar helpers — one source of truth for the initials shown when a
// crew member has no profile photo, plus the storage bucket name.
//
// Before this file, initials were computed ad-hoc in ~10 places across web +
// mobile (four near-identical `getInitials` copies plus a scatter of
// `name.charAt(0)` one-offs). The <CrewAvatar> components (web + mobile) render
// the profile photo when present and fall back to `getInitials` otherwise, so
// every call site now agrees on the same fallback.

/** Storage bucket that holds crew profile photos. Private; read via signed URLs. */
export const CREW_AVATARS_BUCKET = "crew-avatars";

/** Allowed image MIME types for a profile photo (kept in sync with the bucket + RLS). */
export const CREW_AVATAR_ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"] as const;

/** Max profile-photo size in bytes (kept in sync with the bucket's file_size_limit). */
export const CREW_AVATAR_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

const AVATAR_EXT_BY_MIME: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

/**
 * File extension for an allowed avatar MIME type, or null when the type is not
 * an accepted image format. Used to build the storage object key and to reject
 * unsupported uploads before any bytes leave the device.
 */
export function avatarExtForMime(mime: string | null | undefined): string | null {
  if (!mime) return null;
  return AVATAR_EXT_BY_MIME[mime.toLowerCase()] ?? null;
}

/**
 * Initials for a crew member's display name, used as the avatar fallback.
 *
 * Rules (unified from the previous web + mobile implementations):
 *   - blank / missing name            → "?"
 *   - single word (e.g. "Davy")       → first two letters ("DA")
 *   - two+ words ("Davy Rietdijk")    → first letter of first + last word ("DR")
 *
 * Always upper-cased. Safe against extra whitespace and single-character words.
 */
export function getInitials(name: string | null | undefined): string {
  const cleaned = (name ?? "").trim();
  if (!cleaned) return "?";

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  const first = parts[0][0] ?? "";
  const last = parts[parts.length - 1][0] ?? "";
  return (first + last).toUpperCase();
}

/**
 * The first word of a display name ("Lisa Mulder" → "Lisa"), for the short
 * labels where a full name does not fit (chips, "Open Lisa's schedule").
 * "" for a blank name.
 */
export function getFirstName(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/**
 * Build the storage object key for a crew member's avatar. The `<user_id>`
 * prefix is what the storage RLS policies parse to enforce ownership, so it
 * MUST be the uploader's own auth uid.
 */
export function crewAvatarPath(userId: string, fileName: string): string {
  return `${userId}/${fileName}`;
}
